/* =====================================================================
 * ImageLightbox — 이미지를 화면 가득 확대해 보는 오버레이.
 *
 * 채팅 결과 이미지(`MessageBubble` 내부 lightbox)와 같은 모양을 쓴다:
 * 어두운 배경 · 가운데 이미지 · 오른쪽 위 닫기 · 좌우 이동 · 아래 번호.
 * 여기에 캡션 한 줄을 더했다. 사용자 가이드처럼 이미지마다 설명이 붙는
 * 문서에서 "지금 무엇을 보고 있는지" 를 잃지 않기 위함이다.
 *
 * 닫기: Esc · 배경 클릭 · 닫기 버튼. 이동: ← → · 좌우 버튼.
 * 짧은 조작 영상(`kind: 'video'`)도 같은 자리에서 크게 재생한다.
 * ===================================================================== */

import React, { useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled, { keyframes } from 'styled-components';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';

export interface ImageLightboxItem {
  /** 원본 해상도로 그린다(화면을 넘으면 줄인다). 확대가 목적이라 srcSet 을 받지 않는다. */
  readonly src: string;
  /** 짧은 조작 영상이면 `video` — 자동 재생 · 반복 · 음소거로 크게 보여준다. */
  readonly kind?: 'image' | 'video';
  readonly alt?: string;
  readonly caption?: string;
}

export interface ImageLightboxProps {
  readonly items: readonly ImageLightboxItem[];
  /** 열려 있는 이미지 위치. `null` 이면 닫힌 상태. */
  readonly index: number | null;
  readonly onIndexChange: (index: number | null) => void;
  readonly closeLabel?: string;
  readonly prevLabel?: string;
  readonly nextLabel?: string;
}

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.88);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 56px 72px;
  animation: ${fadeIn} 0.2s ease;
  cursor: zoom-out;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 56px 12px;
  }
`;

const Media = styled.img`
  max-width: 100%;
  max-height: calc(100vh - 150px);
  object-fit: contain;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.5);
  cursor: default;
  user-select: none;
`;

const Caption = styled.div`
  max-width: 720px;
  font-size: 0.8125rem;
  line-height: 1.5;
  text-align: center;
  color: rgba(255, 255, 255, 0.82);
  cursor: default;
`;

const RoundButton = styled.button`
  position: fixed;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: rgba(255, 255, 255, 0.22);
  }

  &:focus-visible {
    outline: 2px solid #fff;
    outline-offset: 2px;
  }
`;

const CloseButton = styled(RoundButton)`
  top: 20px;
  right: 20px;
`;

const NavButton = styled(RoundButton)<{ $side: 'left' | 'right' }>`
  top: 50%;
  ${({ $side }) => $side}: 20px;
  width: 44px;
  height: 44px;
  transform: translateY(-50%);

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    ${({ $side }) => $side}: 8px;
  }
`;

const Counter = styled.div`
  position: fixed;
  top: 30px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 0.8rem;
  letter-spacing: 0.05em;
  color: rgba(255, 255, 255, 0.65);
`;

export const ImageLightbox: React.FC<ImageLightboxProps> = ({
  items,
  index,
  onIndexChange,
  closeLabel = '닫기',
  prevLabel = '이전 이미지',
  nextLabel = '다음 이미지',
}) => {
  const open = index !== null && index >= 0 && index < items.length;

  const close = useCallback(() => onIndexChange(null), [onIndexChange]);
  const go = useCallback(
    (delta: number) => {
      if (index === null) return;
      const next = index + delta;
      if (next >= 0 && next < items.length) onIndexChange(next);
    },
    [index, items.length, onIndexChange],
  );

  useEffect(() => {
    if (!open) return undefined;
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
      if (event.key === 'ArrowRight') go(1);
      if (event.key === 'ArrowLeft') go(-1);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, close, go]);

  if (!open || typeof document === 'undefined') return null;
  const item = items[index];
  const stop = (event: React.MouseEvent) => event.stopPropagation();

  return createPortal(
    <Overlay role="dialog" aria-modal="true" aria-label={item.caption ?? item.alt} onClick={close}>
      <CloseButton type="button" aria-label={closeLabel} title={closeLabel} onClick={close}>
        <X size={20} />
      </CloseButton>
      {items.length > 1 && (
        <Counter>
          {index + 1} / {items.length}
        </Counter>
      )}
      {index > 0 && (
        <NavButton
          type="button"
          $side="left"
          aria-label={prevLabel}
          onClick={(event) => {
            stop(event);
            go(-1);
          }}
        >
          <ChevronLeft size={22} />
        </NavButton>
      )}
      {item.kind === 'video' ? (
        <Media as="video" src={item.src} autoPlay loop muted playsInline controls aria-label={item.alt} onClick={stop} />
      ) : (
        <Media src={item.src} alt={item.alt ?? ''} onClick={stop} />
      )}
      {item.caption && <Caption onClick={stop}>{item.caption}</Caption>}
      {index < items.length - 1 && (
        <NavButton
          type="button"
          $side="right"
          aria-label={nextLabel}
          onClick={(event) => {
            stop(event);
            go(1);
          }}
        >
          <ChevronRight size={22} />
        </NavButton>
      )}
    </Overlay>,
    document.body,
  );
};
